import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Compass,
  FileText,
  Home,
  Inbox as InboxIcon,
  Plus,
  UserRoundSearch,
  Users,
  X,
} from "lucide-react";
import { useAuthState } from "../hooks/useAuth";
import ExitButton from "./ExitButton";
import MySpaceButton from "./MySpaceButton";
import MyContentButton from "./MyContentButton";
import CreateContentButton from "./CreateContentButton";
import CommunityButton from "./CommunityButton";
import DiscoverButton from "./DiscoverButton";
import MeetPeopleButton from "./MeetPeopleButton";
import InboxButton from "./InboxButton";
import "./NavigationButtons.css";

const NavigationButtons = ({
  disableAll = false,
  customExitHandler = null,
  hideExitButton = false,
  hideCommunityButton = false,
  hideActiveButton = false,
  hiddenButtons = [],
  communityRouteState = null,
  guestRedirectPath = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuthState();

  if (user?.isActive === false) {
    return null;
  }

  if (guestRedirectPath && loading) {
    return null;
  }
  
  // Determine exit destination based on current page
  const getExitDestination = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const folder = searchParams.get('folder');
    const lessonId = searchParams.get('lessonId');
    const returnTo = searchParams.get('returnTo');
    
    // If returnTo parameter exists, use it
    if (returnTo) {
      return returnTo;
    }
    
    // If in study/notes with lessonId, go to all-notes
    if (location.pathname === '/study/notes' && lessonId && folder) {
      return '/all-notes';
    }
    
    // Level 1 pages go to home
    if (['/myspace', '/userfolders', '/create-content'].includes(location.pathname)) {
      return '/';
    }
    // Public lesson pages go to home
    if (location.pathname.startsWith('/public/lesson/')) {
      return '/';
    }
    // All other pages go to home
    return '/';
  }, [location.pathname, location.search]);
  
  // Determine which button should be disabled
  const isMySpace = location.pathname === '/myspace';
  const isUserFolders = location.pathname === '/userfolders';
  const isCreateContent = location.pathname === '/create-content';
  const isCommunity = location.pathname === '/communities';
  const isDiscover = location.pathname.startsWith("/discover-communities");
  const isMeetPeople = location.pathname.startsWith("/meet-people");
  const isInbox = location.pathname === "/inbox";
  const shouldHideButton = React.useCallback(
    (buttonKey, isActive = false) =>
      hiddenButtons.includes(buttonKey) || (hideActiveButton && isActive),
    [hiddenButtons, hideActiveButton],
  );

  const guestReturnPath = `${location.pathname}${location.search}${location.hash}`;
  const handleGuestRedirect = React.useCallback(
    (target) => {
      navigate(guestRedirectPath, {
        state: { returnTo: guestReturnPath, target },
      });
    },
    [guestRedirectPath, guestReturnPath, navigate],
  );
  
  const handleExitClick = React.useCallback(() => {
    if (customExitHandler) {
      customExitHandler();
    } else {
      navigate(getExitDestination());
    }
  }, [customExitHandler, navigate, getExitDestination]);

  if (!loading && !user && guestRedirectPath) {
    const guestButtons = [
      {
        key: "exit",
        className: "nav-button nav-button-exit",
        title: "Sign up",
        icon: X,
        hidden: true,
      },
      {
        key: "myspace",
        className: "nav-button nav-button-myspace",
        title: "Sign up to open My Space",
        icon: Home,
        hidden: shouldHideButton("myspace", isMySpace),
      },
      {
        key: "mycontent",
        className: "nav-button nav-button-mycontent",
        title: "Sign up to open My Content",
        icon: FileText,
        hidden: shouldHideButton("mycontent", isUserFolders),
      },
      {
        key: "create",
        className: "nav-button nav-button-create",
        title: "Sign up to create content",
        icon: Plus,
        hidden: shouldHideButton("create", isCreateContent),
      },
      {
        key: "community",
        className: "nav-button nav-button-community",
        title: "Sign up to open Community",
        icon: Users,
        hidden:
          hideCommunityButton || shouldHideButton("community", isCommunity),
      },
      {
        key: "discover",
        className: "nav-button nav-button-discover",
        title: "Sign up to discover communities",
        icon: Compass,
        hidden: shouldHideButton("discover"),
      },
      {
        key: "meet",
        className: "nav-button nav-button-meet",
        title: "Sign up to meet people",
        icon: UserRoundSearch,
        hidden: shouldHideButton("meet"),
      },
      {
        key: "inbox",
        className: "inbox-button inbox-button--navigation",
        title: "Sign up to open Inbox",
        icon: InboxIcon,
        hidden: shouldHideButton("inbox", isInbox),
      },
    ];

    return (
      <>
        {guestButtons
          .filter((button) => !button.hidden)
          .map(({ key, className, title, icon: Icon }) => (
            <button
              key={key}
              className={className}
              onClick={() => handleGuestRedirect(key)}
              title={title}
              aria-label={title}
              type="button"
            >
              <Icon size={18} strokeWidth={2} />
            </button>
          ))}
      </>
    );
  }
  
  return (
    <>
      {!hideExitButton && !(guestRedirectPath && !user) && (
        <ExitButton
          onClick={handleExitClick}
          title="Back"
          disabled={disableAll}
        />
      )}
      {!shouldHideButton("myspace", isMySpace) && (
        <MySpaceButton disabled={disableAll || isMySpace} />
      )}
      {!shouldHideButton("mycontent", isUserFolders) && (
        <MyContentButton disabled={disableAll || isUserFolders} />
      )}
      {!shouldHideButton("create", isCreateContent) && (
        <CreateContentButton disabled={disableAll || isCreateContent} />
      )}
      {!hideCommunityButton && !shouldHideButton("community", isCommunity) && (
        <CommunityButton
          disabled={disableAll || isCommunity}
          navigationState={communityRouteState}
        />
      )}
      {!shouldHideButton("discover") && (
        <DiscoverButton disabled={disableAll || isDiscover} />
      )}
      {!shouldHideButton("meet") && (
        <MeetPeopleButton disabled={disableAll || isMeetPeople} />
      )}
      {!shouldHideButton("inbox", isInbox) && (
        <InboxButton variant="navigation" disabled={disableAll || isInbox} />
      )}
    </>
  );
};

export default NavigationButtons;
